import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "bokeh-drift";

const DEFAULT_BG = "#1a1033";
const DEFAULT_BOKEH_A = "#fb7185";
const DEFAULT_BOKEH_B = "#a78bfa";
const DEFAULT_BOKEH_C = "#fde68a";

const ORB_DENSITY = 0.00006; // orbs per pixel² of canvas
const MIN_ORBS = 16;
const MAX_ORBS = 70;

function rand(min, max) { return min + Math.random() * (max - min); }
function randChoice(arr) { return arr[(Math.random() * arr.length) | 0]; }

export default function BokehDrift({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const orbsRef = useRef([]);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_BOKEH_A, DEFAULT_BOKEH_B, DEFAULT_BOKEH_C]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_BOKEH_A),
      pickColor(colors, 2, DEFAULT_BOKEH_B),
      pickColor(colors, 3, DEFAULT_BOKEH_C),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let t = 0;

    const seedOrbs = () => {
      const target = Math.round(width * height * ORB_DENSITY);
      const count = Math.max(MIN_ORBS, Math.min(MAX_ORBS, target));
      const arr = new Array(count);
      for (let i = 0; i < count; i += 1) {
        // depth: 0 = far/blurry/big/dim, 1 = near/sharper/small/bright.
        // Skewing toward small depths gives more atmospheric "background"
        // orbs and only a few crisp ones up front.
        const depth = Math.pow(Math.random(), 1.6);
        const radius = (1 - depth) * rand(40, 110) + depth * rand(8, 22);
        arr[i] = {
          x: rand(0, width),
          y: rand(0, height),
          // Parallax: deeper orbs drift slower than near orbs.
          vx: rand(-0.25, 0.25) * (0.4 + depth),
          vy: rand(-0.18, 0.18) * (0.4 + depth) - 0.05, // gentle upward bias
          radius,
          depth,
          color: randChoice([1, 2, 3]),
          // Each orb breathes alpha on its own period+phase.
          phase: rand(0, Math.PI * 2),
          period: rand(0.5, 1.4),
        };
      }
      // Sort back-to-front so near orbs render on top — sells the depth.
      arr.sort((a, b) => a.depth - b.depth);
      orbsRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedOrbs();
    };

    const tick = () => {
      const [bg, c1, c2, c3] = colorsRef.current;

      // Background — subtle radial vignette so the centre feels like the
      // photographic focus point and the edges fall off.
      const vg = ctx.createRadialGradient(
        width / 2, height / 2, 0,
        width / 2, height / 2, Math.max(width, height) * 0.7
      );
      vg.addColorStop(0, bg);
      vg.addColorStop(1, rgba(bg, 0.85));
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, width, height);

      const orbs = orbsRef.current;

      ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < orbs.length; i += 1) {
        const o = orbs[i];
        o.x += o.vx;
        o.y += o.vy;
        // Soft wrap so orbs reappear smoothly on the opposite side.
        if (o.x < -o.radius) o.x = width + o.radius;
        if (o.x > width + o.radius) o.x = -o.radius;
        if (o.y < -o.radius) o.y = height + o.radius;
        if (o.y > height + o.radius) o.y = -o.radius;

        const breathe = 0.65 + 0.35 * Math.sin(t * o.period + o.phase);
        // Brighter when closer (near orbs are crisp + intense).
        const peak = (0.18 + o.depth * 0.55) * breathe;

        let color;
        if (o.color === 1) color = c1;
        else if (o.color === 2) color = c2;
        else color = c3;

        const grad = ctx.createRadialGradient(o.x, o.y, 0, o.x, o.y, o.radius);
        grad.addColorStop(0, rgba(color, peak));
        // Inner soft ring — gives the classic donut-ish bokeh look on the
        // larger orbs. Smaller orbs show this less because they fade fast.
        grad.addColorStop(0.55, rgba(color, peak * 0.45));
        grad.addColorStop(1, rgba(color, 0));
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(o.x, o.y, o.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = "source-over";

      t += 0.016;
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
